import { css, keyframes } from '@emotion/react'
import { typo } from '@mathflat/common/style'

// 드롭다운 메뉴 등장 (레퍼런스 svcPopIn)
const popIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-6px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const S = {
  wrap: css`
    position: relative;
    display: inline-flex;
    // face 의 3D 플립 합성 레이어 요동이 답안 이미지로 번지지 않도록 별도 stacking context 로 격리한다.
    // 단 isolation 은 드롭다운(menu, z-index:100)을 이 컨텍스트에 가두므로,
    // wrap 자체를 z-index 로 끌어올려 메뉴가 아래 페이지 콘텐츠 위에 그려지도록 한다. iOS Chrome 깜빡임 방어.
    isolation: isolate;
    z-index: 100;
  `,
  trigger: css`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    box-sizing: border-box;
    width: 80px;
    height: 36px;
    padding: 0 12px;
    border: 1px solid var(--Colors-Gray-300);
    border-radius: var(--Radius-200);
    background-color: var(--White);
    color: var(--Colors-Gray-900);
    font-size: 14px;
    line-height: 21px;
    perspective: 240px;
    transition:
      background-color 0.15s,
      border-color 0.15s,
      box-shadow 0.15s;

    &:hover {
      background-color: var(--Colors-Gray-100);
      border-color: var(--Colors-Gray-300);
    }

    svg {
      transition: transform 0.2s ease-in-out;
    }

    &.opened {
      border-color: var(--Colors-Sky-400);
      box-shadow: 0 0 0 3px var(--Colors-Sky-100);
    }

    &.opened svg {
      transform: rotate(-180deg);
    }
  `,
  face: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 24px;
    // flip 은 Web Animations API 로 재생한다 — 로고 재요청을 막으려 face 를 remount 하지 않기 때문.
    // 플립을 전용 합성 레이어에 올려 GPU 변형만으로 처리한다. 레이어 승격을 미리 확정해
    // 애니메이션 중 이웃 레이어(답안 이미지) 재합성을 유발하지 않도록 한다. iOS Chrome 깜빡임 방어.
    backface-visibility: hidden;
    will-change: transform, opacity;
  `,
  label: css`
    font-size: 14px;
    line-height: 21px;
    white-space: nowrap;
  `,
  labelWithLogo: css`
    font-weight: 500;
  `,
  logoSlot: css`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;

    &.text-logo {
      width: 100%;
    }

    .text {
      ${typo.caption02};
      font-weight: 700;
      border-radius: var(--Radius-100);
      background-color: var(--Colors-Violet-500);
      color: var(--White);
      padding: 2px 4px;
    }
  `,
  triggerLogo: css`
    position: absolute;
    inset: 0;
    width: 16px;
    height: 16px;
    object-fit: contain;
    transition: opacity 0.15s;
  `,
  // 스쿨플랫 깃발은 배경이 없어 과목 색을 직접 입힌다(radius 는 30px 심볼의 8px 을 16px 로 환산).
  // 깃발 SVG 는 viewBox 20 안에 15 크기라 렌더 폭의 75% 를 차지한다. 드롭다운의 배경:깃발 = 2:1 비율을
  // 16px 에서도 맞추려면 깃발이 8px 이어야 해서, content 를 10.5px 로 줄이는 padding 을 준다.
  triggerLogoTinted: css`
    padding: 2.75px;
    border-radius: var(--Radius-100);
  `,
  menu: css`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 100;
    display: flex;
    flex-direction: column;
    min-width: 232px;
    padding: 10px;
    border: 1px solid var(--Colors-Gray-300);
    border-radius: var(--Radius-200);
    background-color: var(--White);
    box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.2);
    animation: 0.16s ease-out ${popIn};
  `,
  menuHeader: css`
    ${typo.caption01};
    padding: 8px 10px;
    color: var(--Colors-Gray-600);
  `,
  item: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    border-radius: var(--Radius-200);
    text-align: left;
    white-space: nowrap;
    cursor: default;

    &.actionable {
      cursor: pointer;
    }

    &:not(.current):hover {
      background-color: var(--Colors-Gray-100);
    }
  `,
  itemMain: css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
  `,
  symbol: css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: 30px;
    height: 30px;
    border: 1px solid var(--Colors-Gray-200);
    border-radius: var(--Radius-200);
    background-color: var(--White);
    overflow: hidden;

    img {
      width: 20px;
      height: 20px;
      object-fit: contain;
    }
  `,
  // 흰 카드·테두리 대신 과목 색 배경을 채운다. 깃발 SVG 는 자체 여백을 갖고 있어 크기는 그대로 둔다.
  symbolTinted: css`
    border-color: transparent;
  `,
  itemText: css`
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  brandName: css`
    ${typo.body02};
    font-weight: 700;
    color: var(--Colors-Gray-900);
  `,
  subjectLabel: css`
    ${typo.caption01};
    color: var(--Colors-Gray-600);
  `,
  itemAside: css`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 4px;
  `,
  usingText: css`
    ${typo.caption01};
    color: var(--Colors-Gray-600);
  `,
}

export default S
